
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserInvoiceEntity from 'Models/Security/Acl/UserInvoiceEntity';
import VisitorsInvoiceEntity from 'Models/Security/Acl/VisitorsInvoiceEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import moment from 'moment';
import { LocaleFormatCurrency } from 'Util/StringUtils';

export interface IInvoiceEntityAttributes extends IModelAttributes {
	referredBy: string;
	customerCount: number;
	monthlyExposure: number;
	usingApprove: boolean;
	usingMonitor: boolean;
	usingPPSR: boolean;
	userCount: number;
	discountPrice: number;
	finalPrice: number;
	lineItems: string;
	monthlyPrice: number;

	organisationId: string;
	organisation: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;

}

@entity('InvoiceEntity', 'Invoice')
export default class InvoiceEntity extends Model
	implements IInvoiceEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserInvoiceEntity(),
		new VisitorsInvoiceEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * The name of the referral partner that referred this Organisation
	 */
	@observable
	@attribute<InvoiceEntity, string>()
	@CRUD({
		name: 'Referred By',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public referredBy: string;
	

	
	/**
	 * Customer Count
	 */
	@Validators.Integer()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'Customer Count',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public customerCount: number;
	

	
	/**
	 * Monthly Exposure
	 */
	@Validators.Numeric()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'Monthly Exposure',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public monthlyExposure: number;
	

	
	/**
	 * Using Approve
	 */
	@observable
	@attribute<InvoiceEntity, boolean>()
	@CRUD({
		name: 'Using Approve',
		displayType: 'checkbox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public usingApprove: boolean;
	

	
	/**
	 * Using Monitor
	 */
	@observable
	@attribute<InvoiceEntity, boolean>()
	@CRUD({
		name: 'Using Monitor',
		displayType: 'checkbox',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public usingMonitor: boolean;
	

	
	/**
	 * Using PPSR
	 */
	@observable
	@attribute<InvoiceEntity, boolean>()
	@CRUD({
		name: 'Using PPSR',
		displayType: 'checkbox',
		order: 60,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public usingPPSR: boolean;
	

	
	/**
	 * User Count
	 */
	@Validators.Integer()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'User Count',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public userCount: number;
	

	
	/**
	 * Discount Price
	 */
	@Validators.Numeric()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'Discount Price',
		displayType: 'textfield',
		order: 80,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public discountPrice: number;
	

	
	/**
	 * Final Price
	 */
	@Validators.Numeric()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'Final Price',
		displayType: 'textfield',
		order: 90,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public finalPrice: number;
	

	
	/**
	 * Line items that make up the Final Price
	 */
	@observable
	@attribute<InvoiceEntity, string>()
	@CRUD({
		name: 'Line Items',
		displayType: 'textfield',
		order: 100,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public lineItems: string;
	

	
	/**
	 * Monthly Price
	 */
	@Validators.Numeric()
	@observable
	@attribute<InvoiceEntity, number>()
	@CRUD({
		name: 'Monthly Price',
		displayType: 'textfield',
		order: 110,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public monthlyPrice: number;
	

	/**
	 * Invoice
	 */
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 120,
		referenceTypeFunc: () => Models.OrganisationEntity,
		
	})
	public organisationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IInvoiceEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IInvoiceEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.referredBy !== undefined) {
				this.referredBy = attributes.referredBy;
			}
			if (attributes.customerCount !== undefined) {
				this.customerCount = attributes.customerCount;
			}
			if (attributes.monthlyExposure !== undefined) {
				this.monthlyExposure = attributes.monthlyExposure;
			}
			if (attributes.usingApprove !== undefined) {
				this.usingApprove = attributes.usingApprove;
			}
			if (attributes.usingMonitor !== undefined) {
				this.usingMonitor = attributes.usingMonitor;
			}
			if (attributes.usingPPSR !== undefined) {
				this.usingPPSR = attributes.usingPPSR;
			}
			if (attributes.userCount !== undefined) {
				this.userCount = attributes.userCount;
			}
			if (attributes.discountPrice !== undefined) {
				this.discountPrice = attributes.discountPrice;
			}
			if (attributes.finalPrice !== undefined) {
				this.finalPrice = attributes.finalPrice;
			}
			if (attributes.lineItems !== undefined) {
				this.lineItems = attributes.lineItems;
			}
			if (attributes.monthlyPrice !== undefined) {
				this.monthlyPrice = attributes.monthlyPrice;
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public listExpands = `
		organisation {
			id
			primaryBusinessEntity {
				id
				name
			}
		}
	`;

	public defaultExpands = `
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}

			primaryBusinessEntity {
				id
				name
			}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		const organisationName = this.organisation?.getDisplayName() ?? this.organisationId;
		return [
			moment(this.created).format('YYYY-MM'),
			LocaleFormatCurrency(this.finalPrice),
			organisationName,
		].join(' | ');
		// % protected region % [Customise the display name for this entity] end
	}


}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(InvoiceEntity.prototype, 'created');
CRUD(modifiedAttr)(InvoiceEntity.prototype, 'modified');
